import { useEffect } from "react";

import { track } from "@/lib/analytics";

/**
 * `outbound_click {href_host, kind}` — one delegated listener for every anchor
 * on the site, so no component has to remember to instrument its own links.
 *
 * What leaves the browser is the HOST and nothing else:
 *
 *   · `mailto:` → `{ href_host: "mailto", kind: "mailto" }`. The address is
 *     never sent. There is exactly one public address on the site today
 *     (the Program tier's contact link), and an email address is personal data
 *     even when it is ours.
 *   · An http(s) link to another origin → `{ href_host: <host>, kind:
 *     "external" }`. Host only: a path or a query string on an outbound link
 *     is free text, which this contract does not send.
 *   · A same-origin link fires nothing — that is a page_view, and it is
 *     already counted as one.
 *   · Any other scheme (tel:, sms:, chrome-extension:, javascript:) fires
 *     nothing.
 *
 * Capture phase, so a component that calls stopPropagation() on its own click
 * handler does not silently remove itself from the measurement.
 */
export function useOutboundClicks(): void {
  useEffect(() => {
    if (typeof document === "undefined") return;

    const onClick = (event: MouseEvent) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const anchor = target.closest("a");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href) return;

      if (href.trim().toLowerCase().startsWith("mailto:")) {
        track("outbound_click", { href_host: "mailto", kind: "mailto" });
        return;
      }

      let url: URL;
      try {
        url = new URL(href, window.location.href);
      } catch {
        return;
      }
      if (url.protocol !== "http:" && url.protocol !== "https:") return;
      if (url.host === window.location.host) return;

      track("outbound_click", { href_host: url.host, kind: "external" });
    };

    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);
}
